import { useState, useRef, useEffect } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiX } from 'react-icons/fi';
import { api } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';
import PopupForm from './PopupForm';

const PopupList = ({ popups = [], fetchPopups, loading = false }) => {
  const [selectedPopup, setSelectedPopup] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const panelRef = useRef(null);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this popup?')) {
      setIsDeleting(true);
      try {
        await api.delete(`/api/popups/${id}`);
        fetchPopups();
      } catch (error) {
        console.error('Error deleting popup:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    if (showForm && panelRef.current) {
      setTimeout(() => {
        panelRef.current.style.transform = 'translateX(0)';
      }, 10);
    }
  }, [showForm]);

  const closePanel = () => {
    if (panelRef.current) {
      panelRef.current.style.transform = 'translateX(100%)';
      setTimeout(() => {
        setShowForm(false);
      }, 300);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden relative">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Popup Updates</h3>
        <button
          onClick={() => {
            setSelectedPopup(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          <FiPlus className="mr-1" /> Create Popup
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Image
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Link
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                End Date
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#166534]"></div>
                  </div>
                </td>
              </tr>
            ) : popups && popups.length > 0 ? (
              popups.map((popup) => (
                <tr key={popup._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {popup.image ? (
                      <img 
                        src={getImageSrc(popup.image)}
                        alt="Popup"
                        className="h-12 w-12 object-cover rounded"
                        onError={(e) => {
                          console.error('Image load error:', popup._id);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-xs text-gray-500">No Image</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {popup.title || 'Untitled Popup'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {popup.link ? (
                      <a
                        href={popup.link.startsWith('http') ? popup.link : `https://${popup.link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#166534] hover:underline"
                      >
                        {popup.link.length > 30 
                          ? `${popup.link.substring(0, 30)}...` 
                          : popup.link}
                      </a>
                    ) : (
                      <span className="text-sm text-gray-400">No link</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      popup.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {popup.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {popup.endDate 
                        ? new Date(popup.endDate).toLocaleDateString() 
                        : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedPopup(popup);
                        setShowForm(true);
                      }}
                      className="text-[#166534] hover:text-[#0e3d1f] mr-3"
                      aria-label="Edit popup"
                    >
                      <FiEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(popup._id)}
                      disabled={isDeleting}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      aria-label="Delete popup"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  {popups ? 'No popups found' : 'Error loading popups'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
              onClick={closePanel}
            />
            
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <div
                ref={panelRef}
                className="relative w-screen max-w-md transform transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                style={{ 
                  transform: 'translateX(calc(100% + 20px))',
                  marginLeft: '-20px'
                }}
              >
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {selectedPopup ? 'Edit Popup' : 'Create New Popup'}
                    </h3>
                    <button
                      onClick={closePanel}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none transition-colors duration-200"
                      aria-label="Close form"
                    >
                      <FiX className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="flex-1 p-4">
                    <PopupForm
                      popup={selectedPopup}
                      onClose={closePanel}
                      onSave={() => {
                        fetchPopups();
                        closePanel();
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupList;